import React, { useEffect, useState } from 'react';

import { faPage } from '@fortawesome/pro-regular-svg-icons';
import { CssBaseline, Drawer, List } from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import cx from 'classnames';

import { IModule } from '@cloud-wave/neon-common-lib';

import { useConfigContext } from 'lib/core/config/Context';

import { useModulesContext } from 'lib/common/contexts/ModulesContext';
import { useDesktopLayoutContext } from 'lib/common/contexts/layout/DesktopLayoutContext';

import { ADMIN_NAV_OPTIONS } from 'lib/common/constants/adminNavOptions';
import DEFAULT_NAV_OPTIONS from 'lib/common/constants/defaultNavOptions';

import { getModuleIcon } from 'lib/common/utils/getModuleIcon';

import SidebarHotkey from './SidebarHotkey';
import SidebarMenuItem from './SidebarMenuItem';

import VARS from 'css/export-vars.module.scss';

import './sidebar.scss';

const drawerWidth = '275px';

const useStyles = ({ relative }) =>
  makeStyles((theme: Theme) => {
    return createStyles({
      root: {
        display: 'flex',
        zIndex: 995,
        position: 'sticky',
        top: 55,
        marginLeft: relative ? 20 : 0,
        height: 'calc(100vh - 55px)',
      },
      drawer: {
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        position: 'relative',
        paddingBottom: 15,
        paddingTop: 15,
        height: '100%',
      },
      drawerOpen: {
        width: drawerWidth,
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
        background: 'white',
        borderRight: `1px solid ${VARS.midGrey}`,
        position: 'relative',
        height: '100%',
      },
      drawerClose: {
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        overflowX: 'hidden',
        width: 60,
        [theme.breakpoints.up('sm')]: {
          width: 60,
        },
        background: 'white',
        borderRight: `1px solid ${VARS.midGrey}`,
        position: 'relative',
      },
      toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: '0 5px',
      },
      navList: {
        width: drawerWidth,
      },
      menuItem: {
        marginBottom: 20,
      },
    });
  });

export default function Sidebar(props) {
  const {
    state: { pageModules, modulesLoaded },
  } = useModulesContext();
  const classes = useStyles({ relative: props.relative })();
  const { isSidebarOpen } = useDesktopLayoutContext();
  const { config } = useConfigContext();
  const [navOptions, setNavOptions] = useState<any>(DEFAULT_NAV_OPTIONS(config));
  const [navLoaded, setNavLoaded] = useState(false);

  useEffect(() => {
    if (navLoaded || !modulesLoaded) {
      return;
    }

    setNavOptions([
      ...navOptions,
      ...pageModules.map((pageModule: IModule) => ({
        link: `/${pageModule.pathName}`,
        name: pageModule.name,
        activeClassName: 'nav-active',
        icon: getModuleIcon({ icon: pageModule.icon, defaultIcon: faPage }),
      })),
      ADMIN_NAV_OPTIONS,
    ]);

    setNavLoaded(true);
  }, [modulesLoaded]);

  return (
    <div className={classes.root}>
      <CssBaseline />
      <Drawer
        variant="permanent"
        className={cx(classes.drawer, {
          [classes.drawerOpen]: isSidebarOpen,
          [classes.drawerClose]: !isSidebarOpen,
        })}
        classes={{
          paper: cx({
            [classes.drawerOpen]: isSidebarOpen,
            [classes.drawerClose]: !isSidebarOpen,
          }),
        }}
      >
        {navLoaded && (
          <List component="nav" disablePadding>
            {navOptions.map((item, index) => (
              <React.Fragment key={item.link}>
                <SidebarMenuItem {...item} userdata={props.user} isSidebarOpen={isSidebarOpen} />
                <SidebarHotkey
                  link={item.link}
                  pageHasSubMenu={item.pageHasSubMenu}
                  items={item.items}
                  hotkey={`${index + 1}`}
                />
              </React.Fragment>
            ))}
          </List>
        )}
      </Drawer>
    </div>
  );
}
